import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, ArrowRight, ArrowLeft, Send, Search } from "lucide-react";
import { useAuthGuard } from "../../services/user";
import {
  submitLeaderApplication,
  getLeaderApplicationStatus,
} from "../../services/leaderApplication";
import { StickyHeader } from "../../intro/components/landing-new";

const QUESTIONS = [
  {
    key: "leader_apply_q1",
    defaultValue:
      "Tell us about yourself — what do you do, what are you passionate about, and what excites you most about life right now?",
  },
  {
    key: "leader_apply_q2",
    defaultValue:
      "Tell us about a trip or experience where you stepped outside your comfort zone. What drew you to it and what did you discover?",
  },
  {
    key: "leader_apply_q3",
    defaultValue:
      "Describe a time you brought people together or created a memorable experience for others. What happened?",
  },
  {
    key: "leader_apply_q4",
    defaultValue: "In your own words, what do you think it means to be a Sotravel leader?",
  },
  {
    key: "leader_apply_q5",
    defaultValue:
      "Finally, if you asked your friends to describe you using 5 words, what would they say?",
  },
];

// Languages an applicant can speak. Common languages first. Stored as ISO codes on
// users.languages_spoken. Labels are localized via i18next.
const LANGUAGE_OPTIONS = [
  { code: "en", key: "lang_option_en", defaultValue: "English" },
  { code: "zh", key: "lang_option_zh", defaultValue: "Chinese" },
  { code: "ms", key: "lang_option_ms", defaultValue: "Malay" },
  { code: "ko", key: "lang_option_ko", defaultValue: "Korean" },
  { code: "ja", key: "lang_option_ja", defaultValue: "Japanese" },
  { code: "ta", key: "lang_option_ta", defaultValue: "Tamil" },
  { code: "other", key: "lang_option_other", defaultValue: "Others" },
];

const getCurrentBatch = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const ProgressTracker = ({ currentStep, totalSteps }) => {
  return (
    <div className="tw-flex tw-items-center tw-gap-2">
      {Array.from({ length: totalSteps }).map((_, index) => (
        <div
          key={index}
          className={`tw-h-1.5 tw-rounded-full tw-transition-all tw-duration-300 ${
            index < currentStep
              ? "tw-w-8 tw-bg-primary-main"
              : index === currentStep
                ? "tw-w-8 tw-bg-gray-800"
                : "tw-w-4 tw-bg-gray-300"
          }`}
        />
      ))}
    </div>
  );
};

const IntroPage = ({ onStart, t }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, x: -50 }}
      className="tw-flex tw-flex-1 tw-flex-col tw-overflow-auto"
    >
      {/* Placeholder image */}
      <div className="tw-mb-4 tw-flex tw-h-56 tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-2xl tw-bg-gray-200 md:tw-mb-8 md:tw-h-80">
        <img
          src="/images/duck_fam_pic.jpg"
          alt=""
          className="tw-h-full tw-w-full tw-object-cover"
        />
      </div>

      {/* Title */}
      <h1 className="tw-mb-3 tw-text-xl tw-font-bold tw-text-gray-900 md:tw-mb-6 md:tw-text-3xl">
        {t("leader_apply_title", { defaultValue: "Leader programme application" })}
      </h1>

      {/* Inspirational message */}
      <p className="tw-text-sm tw-leading-relaxed tw-text-gray-600 md:tw-text-lg">
        {t("leader_apply_disclaimer", {
          defaultValue:
            "Being a Sotravel leader is an extremely rewarding role. But it is not for everyone. As a leader, you have to show up when things are unclear and others look towards you. You have to care about the people around you and not just your own experience. If this is who you are or you aspire to be, we can't wait to meet you.",
        })}
      </p>

      <div className="tw-mt-auto tw-pt-4">
        <button
          onClick={onStart}
          className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-bg-gray-900 tw-px-6 tw-py-3 tw-font-semibold tw-text-white tw-transition-all hover:tw-bg-gray-800 md:tw-py-4"
        >
          {t("leader_apply_start", { defaultValue: "Start application" })}
          <ArrowRight size={18} />
        </button>
      </div>
    </motion.div>
  );
};

const QuestionPage = ({ question, questionIndex, totalQuestions, value, onChange, error, t }) => {
  return (
    <motion.div
      key={questionIndex}
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="tw-flex tw-flex-col"
    >
      <h2 className="tw-mb-4 tw-text-lg tw-font-semibold tw-text-gray-900 md:tw-mb-6 md:tw-text-2xl">
        {t(question.key, { defaultValue: question.defaultValue })}
      </h2>

      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t("leader_apply_placeholder", { defaultValue: "Type your answer here..." })}
        rows={7}
        className="tw-w-full tw-resize-none tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-px-4 tw-py-3 tw-text-base tw-leading-relaxed tw-text-gray-900 tw-placeholder-gray-400 tw-transition-all tw-duration-200 focus:tw-border-gray-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-100 md:tw-py-4"
        autoFocus
      />

      {error && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="tw-mt-2 tw-text-sm tw-text-red-500"
        >
          {error}
        </motion.p>
      )}
    </motion.div>
  );
};

const LanguageSelectPage = ({
  selected,
  onToggle,
  search,
  onSearchChange,
  otherLanguage,
  onOtherLanguageChange,
  error,
  t,
}) => {
  const query = search.trim().toLowerCase();
  const filtered = LANGUAGE_OPTIONS.filter((opt) =>
    t(opt.key, { defaultValue: opt.defaultValue }).toLowerCase().includes(query)
  );

  return (
    <motion.div
      key="languages"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden"
    >
      <h2 className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-gray-900 md:tw-text-2xl">
        {t("leader_apply_lang_title", { defaultValue: "Which languages can you speak?" })}
      </h2>
      <p className="tw-mb-4 tw-text-sm tw-text-gray-500 md:tw-mb-6">
        {t("leader_apply_lang_subtitle", { defaultValue: "Select all that apply." })}
      </p>

      {/* Search */}
      <div className="tw-relative tw-mb-4">
        <Search
          size={18}
          className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t("leader_apply_lang_search_placeholder", {
            defaultValue: "Search languages...",
          })}
          className="tw-w-full tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-py-3 tw-pl-10 tw-pr-4 tw-text-base tw-text-gray-900 tw-placeholder-gray-400 tw-transition-all focus:tw-border-gray-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-100"
          autoFocus
        />
      </div>

      {/* Options */}
      <div className="tw-flex tw-flex-col tw-gap-2 tw-overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="tw-py-4 tw-text-center tw-text-sm tw-text-gray-400">
            {t("leader_apply_lang_no_results", { defaultValue: "No languages found." })}
          </p>
        ) : (
          filtered.map((opt) => {
            const isSelected = selected.includes(opt.code);
            return (
              <button
                key={opt.code}
                type="button"
                onClick={() => onToggle(opt.code)}
                className={`tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-border tw-px-4 tw-py-3 tw-text-left tw-text-base tw-transition-all ${
                  isSelected
                    ? "tw-border-gray-900 tw-bg-gray-900 tw-text-white"
                    : "tw-border-gray-200 tw-bg-white tw-text-gray-900 hover:tw-border-gray-400"
                }`}
              >
                <span>{t(opt.key, { defaultValue: opt.defaultValue })}</span>
                {isSelected && <CheckCircle size={18} className="tw-text-white" />}
              </button>
            );
          })
        )}

        {selected.includes("other") && (
          <input
            type="text"
            value={otherLanguage}
            onChange={(event) => onOtherLanguageChange(event.target.value)}
            placeholder={t("leader_apply_lang_other_placeholder", {
              defaultValue: "Enter another language",
            })}
            maxLength={50}
            className="tw-mt-1 tw-w-full tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-px-4 tw-py-3 tw-text-base tw-text-gray-900 tw-placeholder-gray-400 tw-transition-all focus:tw-border-gray-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-100"
            autoFocus
          />
        )}
      </div>

      {error && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="tw-mt-3 tw-text-sm tw-text-red-500"
        >
          {error}
        </motion.p>
      )}
    </motion.div>
  );
};

const LeaderApplicationApply = () => {
  const { t } = useTranslation();
  const { batch: batchParam } = useParams();
  const batch = useMemo(() => batchParam || getCurrentBatch(), [batchParam]);
  useAuthGuard();

  // -2 = intro page, -1 = languages step, 0-4 = questions
  const [currentStep, setCurrentStep] = useState(-2);
  const [answers, setAnswers] = useState(["", "", "", "", ""]);
  const [languages, setLanguages] = useState([]);
  const [otherLanguage, setOtherLanguage] = useState("");
  const [languageSearch, setLanguageSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [error, setError] = useState("");

  const totalQuestions = QUESTIONS.length;
  const isIntro = currentStep === -2;
  const isLanguageStep = currentStep === -1;
  const isLastStep = currentStep === totalQuestions - 1;
  const currentQuestion = currentStep >= 0 ? QUESTIONS[currentStep] : null;

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await getLeaderApplicationStatus(batch);
        if (res.data?.data) {
          setAlreadyApplied(true);
        }
      } catch (err) {
        // Ignore — user hasn't applied yet
      }
    };
    checkStatus();
  }, [batch]);

  const handleChange = (value) => {
    setAnswers((prev) => {
      const updated = [...prev];
      updated[currentStep] = value;
      return updated;
    });
    setError("");
  };

  const handleStart = () => {
    setCurrentStep(-1);
  };

  const handleToggleLanguage = (code) => {
    setLanguages((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
    setError("");
  };

  const handleLanguageNext = () => {
    if (languages.length === 0) {
      setError(
        t("leader_apply_lang_error_empty", {
          defaultValue: "Please select at least one language.",
        })
      );
      return;
    }
    if (languages.includes("other") && !otherLanguage.trim()) {
      setError(
        t("leader_apply_lang_error_other_empty", {
          defaultValue: "Please enter the other language.",
        })
      );
      return;
    }
    setError("");
    setCurrentStep(0);
  };

  const handleNext = () => {
    if (!answers[currentStep].trim()) {
      setError(
        t("leader_apply_error_empty_single", {
          defaultValue: "Please answer this question before continuing.",
        })
      );
      return;
    }
    setError("");
    setCurrentStep((prev) => Math.min(prev + 1, totalQuestions - 1));
  };

  const handleBack = () => {
    setError("");
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!answers[currentStep].trim()) {
      setError(
        t("leader_apply_error_empty_single", {
          defaultValue: "Please answer this question before continuing.",
        })
      );
      return;
    }

    setError("");
    setSubmitting(true);
    try {
      const submittedLanguages = languages.map((language) =>
        language === "other" ? `other:${otherLanguage.trim()}` : language
      );

      await submitLeaderApplication({
        batch,
        answer_1: answers[0],
        answer_2: answers[1],
        answer_3: answers[2],
        answer_4: answers[3],
        answer_5: answers[4],
        languages: submittedLanguages,
      });
      setSubmitted(true);
    } catch (err) {
      if (err.response?.status === 409) {
        setAlreadyApplied(true);
      } else {
        setError(
          t("leader_apply_error_generic", {
            defaultValue: "Something went wrong. Please try again.",
          })
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (alreadyApplied) {
    return (
      <div className="tw-min-h-screen tw-bg-gray-50">
        <StickyHeader withoutCta />
        <div className="tw-flex tw-min-h-[80vh] tw-items-center tw-justify-center tw-px-4 tw-pt-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="tw-max-w-lg tw-text-center"
          >
            <div className="tw-mx-auto tw-mb-6 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-bg-green-100">
              <CheckCircle className="tw-text-green-600" size={32} />
            </div>
            <h2 className="tw-mb-4 tw-text-2xl tw-font-bold tw-text-gray-900">
              {t("leader_apply_already_submitted_title", {
                defaultValue: "You've already applied",
              })}
            </h2>
            <p className="tw-text-gray-600">
              {t("leader_apply_already_submitted_desc", {
                defaultValue:
                  "You have already submitted your application for this batch. We'll be in touch!",
              })}
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="tw-min-h-screen tw-bg-gray-50">
        <StickyHeader withoutCta />
        <div className="tw-flex tw-min-h-[80vh] tw-items-center tw-justify-center tw-px-4 tw-pt-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="tw-max-w-lg tw-text-center"
          >
            <div className="tw-mx-auto tw-mb-6 tw-flex tw-h-20 tw-w-20 tw-items-center tw-justify-center tw-rounded-full tw-bg-green-500">
              <CheckCircle className="tw-text-white" size={40} strokeWidth={1.5} />
            </div>
            <h2 className="tw-mb-4 tw-text-3xl tw-font-bold tw-text-gray-900">
              {t("leader_apply_success_title", { defaultValue: "Application received!" })}
            </h2>
            <p className="tw-mb-4 tw-text-lg tw-text-gray-600">
              {t("leader_apply_success_desc", {
                defaultValue:
                  "Thank you for applying to be a Sotravel leader! We've received your application and our team will review it carefully.",
              })}
            </p>
            <p className="tw-text-gray-500">
              {t("leader_apply_success_contact", {
                defaultValue:
                  "We'll contact you soon via the app and email with next steps. Keep an eye out!",
              })}
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-h-[100dvh] tw-flex-col tw-bg-gray-50">
      <StickyHeader withoutCta />

      <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden tw-px-4 tw-pb-4 tw-pt-20 md:tw-pb-12 md:tw-pt-28">
        <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-2xl tw-flex-1 tw-flex-col">
          {/* Progress header - only show during questions */}
          {currentStep >= 0 && (
            <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between md:tw-mb-8">
              <p className="tw-text-sm tw-font-medium tw-text-gray-500">
                {t("leader_apply_question_of", {
                  defaultValue: "Question {{current}} of {{total}}",
                  current: currentStep + 1,
                  total: totalQuestions,
                })}
              </p>
              <ProgressTracker currentStep={currentStep} totalSteps={totalQuestions} />
            </div>
          )}

          {/* Content */}
          <AnimatePresence mode="wait">
            {isIntro ? (
              <IntroPage key="intro" onStart={handleStart} t={t} />
            ) : isLanguageStep ? (
              <LanguageSelectPage
                key="languages"
                selected={languages}
                onToggle={handleToggleLanguage}
                search={languageSearch}
                onSearchChange={setLanguageSearch}
                otherLanguage={otherLanguage}
                onOtherLanguageChange={(value) => {
                  setOtherLanguage(value);
                  setError("");
                }}
                error={error}
                t={t}
              />
            ) : (
              <QuestionPage
                key={currentStep}
                question={currentQuestion}
                questionIndex={currentStep}
                totalQuestions={totalQuestions}
                value={answers[currentStep]}
                onChange={handleChange}
                error={error}
                t={t}
              />
            )}
          </AnimatePresence>

          {/* Navigation - show on the languages step and during questions */}
          {!isIntro && (
            <div className="tw-mt-4 tw-flex tw-items-center tw-justify-between tw-border-t tw-border-gray-200 tw-pt-4 md:tw-mt-8 md:tw-pt-6">
              <button
                onClick={handleBack}
                className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-px-4 tw-py-2 tw-font-medium tw-text-gray-600 tw-transition-all hover:tw-bg-gray-100 hover:tw-text-gray-900"
              >
                <ArrowLeft size={18} />
                {t("leader_apply_back", { defaultValue: "Back" })}
              </button>

              {isLastStep ? (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-bg-gray-900 tw-px-6 tw-py-3 tw-font-semibold tw-text-white tw-transition-all hover:tw-bg-gray-800 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                >
                  {submitting ? (
                    t("leader_apply_submitting", { defaultValue: "Submitting..." })
                  ) : (
                    <>
                      {t("leader_apply_submit", { defaultValue: "Submit application" })}
                      <Send size={18} />
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={isLanguageStep ? handleLanguageNext : handleNext}
                  className="tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-bg-gray-900 tw-px-6 tw-py-3 tw-font-semibold tw-text-white tw-transition-all hover:tw-bg-gray-800"
                >
                  {t("leader_apply_next", { defaultValue: "Next" })}
                  <ArrowRight size={18} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaderApplicationApply;
